import React from "react";
import { Bar, Line, Pie, Doughnut, Radar, PolarArea } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  RadialLinearScale
} from "chart.js";
import { CircularProgress, Box, IconButton, Tooltip as MuiTooltip, Skeleton } from "@mui/material";
import { Download, Assessment } from "@mui/icons-material";
import MyCustomTooltip from "../../components/admin/admin_dashboard/Tooltip";
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Custom plugin to draw non-overlapping out-labels with connector lines for radial/circular charts
const pieOutlabelsPlugin = {
  id: 'pieOutlabels',
  afterDraw(chart) {
    const { type } = chart.config;
    if (type !== 'pie' && type !== 'doughnut' && type !== 'polarArea') return;

    const { ctx, data } = chart;
    const dataset = data.datasets[0];
    if (!dataset || !dataset.data || dataset.data.length === 0) return;

    const meta = chart.getDatasetMeta(0);
    if (!meta || !meta.data) return;

    ctx.save();
    // High-contrast slate color for text label
    ctx.font = 'bold 9.5px Inter, sans-serif';
    ctx.textBaseline = 'middle';

    const leftLabelsY = [];
    const rightLabelsY = [];

    meta.data.forEach((element, index) => {
      const value = dataset.data[index];
      if (value === undefined || value === null || value === 0) return;

      const label = data.labels[index] || '';
      const formattedVal = Number(value).toLocaleString();
      const truncatedLabel = label.length > 11 ? label.substring(0, 9) + '..' : label;
      const text = `${truncatedLabel}: ${formattedVal}`;

      // Calculate slice middle angle
      const startAngle = element.startAngle;
      const endAngle = element.endAngle;
      const middleAngle = startAngle + (endAngle - startAngle) / 2;

      // Outer radius and center coordinates of the slice
      const outerRadius = element.outerRadius;
      const centerX = element.x;
      const centerY = element.y;

      // Start position (on the boundary of the slice)
      const startX = centerX + Math.cos(middleAngle) * outerRadius;
      const startY = centerY + Math.sin(middleAngle) * outerRadius;

      // End position for the connector (extended out of the slice)
      const extensionLength = 18;
      let endX = centerX + Math.cos(middleAngle) * (outerRadius + extensionLength);
      let endY = centerY + Math.sin(middleAngle) * (outerRadius + extensionLength);

      // Distinguish left vs right side to calculate Y collision checks independently
      const isRightSide = Math.cos(middleAngle) >= 0;
      const labelMinGap = 13; // Minimum vertical gap in pixels between label lines
      const activeYList = isRightSide ? rightLabelsY : leftLabelsY;

      // Sift Y position to resolve collisions
      let adjustedY = endY;
      let collision = true;
      let iterations = 0;
      while (collision && iterations < 35) {
        collision = false;
        for (let i = 0; i < activeYList.length; i++) {
          if (Math.abs(activeYList[i] - adjustedY) < labelMinGap) {
            // Push vertically depending on slice quadrant angle to stretch labels outward
            if (Math.sin(middleAngle) >= 0) {
              adjustedY += 4.5;
            } else {
              adjustedY -= 4.5;
            }
            collision = true;
            break;
          }
        }
        iterations++;
      }
      activeYList.push(adjustedY);
      endY = adjustedY;

      // Draw connector line from slice boundary to label location
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);

      // Short horizontal accent pointer line
      const horizontalSegment = 8;
      const textEndX = isRightSide ? endX + horizontalSegment : endX - horizontalSegment;
      ctx.lineTo(textEndX, endY);

      // Use the slice color for the connector stroke
      ctx.strokeStyle = dataset.borderColor
        ? (Array.isArray(dataset.borderColor) ? dataset.borderColor[index] : dataset.borderColor)
        : '#cbd5e1';
      ctx.lineWidth = 1.3;
      ctx.stroke();

      // Render the label text
      ctx.fillStyle = '#334155';
      if (isRightSide) {
        ctx.textAlign = 'left';
        ctx.fillText(text, textEndX + 4, endY);
      } else {
        ctx.textAlign = 'right';
        ctx.fillText(text, textEndX - 4, endY);
      }
    });

    ctx.restore();
  }
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale
);

const ChartComponents = {
  bar: Bar,
  horizontalBar: Bar,
  line: Line,
  pie: Pie,
  doughnut: Doughnut,
  radar: Radar,
  polarArea: PolarArea,
};

// Highly curated, professional, and visually balanced enterprise-grade color scheme
const COLOR_PALETTE = [
  "#3b82f6", // Premium Soft Blue
  "#0ea5e9", // Sky Blue
  "#10b981", // Sage Emerald
  "#f43f5e", // Sophisticated Rose/Coral
  "#f59e0b", // Warm Amber
  "#8b5cf6", // Soft Violet
  "#0d9488", // Ocean Teal
  "#ea580c", // Burnt Orange
  "#64748b", // Slate Grey
  "#ec4899", // Dusty Pink
  "#a78bfa", // Light Lavender
  "#14b8a6", // Mint Teal
  "#0284c7", // Navy Blue
  "#fb7185", // Light Coral
  "#c084fc", // Orchid Lilac
  "#a3e635", // Soft Lime
  "#059669", // Forest Emerald
  "#475569", // Dark Slate
  "#6366f1"  // Bright Indigo
];

const DashboardChart = ({
  title,
  tooltip = "",
  data,
  type = "bar",
  height = 300, // Balanced, space-conscious standard height
  loading = false,
  stacked = false,
  onDownload = null,
  onClick = null,
  showHeader = true
}) => {
  const ChartComponent = ChartComponents[type] || Bar;

  const [isVisible, setIsVisible] = React.useState(false);
  const chartRef = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (chartRef.current) {
      observer.observe(chartRef.current);
    }
    return () => observer.disconnect();
  }, []);


  const hasY2Axis = React.useMemo(() => {
    return data && data.datasets && data.datasets.some(d => d.yAxisID === 'y2');
  }, [data]);

  // Formats data automatically with distinct, premium colors if none are hardcoded
  const injectedData = React.useMemo(() => {
    if (!data || !data.datasets) return data;
    return {
      ...data,
      datasets: data.datasets.map((dataset, idx) => {
        const color = COLOR_PALETTE[idx % COLOR_PALETTE.length];
        // Convert hex to rgba for a translucent background fill
        const hexToRgba = (hex, alpha) => {
          const r = parseInt(hex.slice(1, 3), 16);
          const g = parseInt(hex.slice(3, 5), 16);
          const b = parseInt(hex.slice(5, 7), 16);
          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };
        
        const isPieDoughnutOrPolar = type === 'pie' || type === 'doughnut' || type === 'polarArea';
        
        const bgCol = isPieDoughnutOrPolar
          ? (dataset.backgroundColor || dataset.data.map((_, i) => COLOR_PALETTE[i % COLOR_PALETTE.length]))
          : (type === 'line' || type === 'radar' || dataset.type === 'line' ? hexToRgba(color, 0.25) : (dataset.backgroundColor || color));
          
        const borderCol = isPieDoughnutOrPolar
          ? (dataset.borderColor || dataset.data.map((_, i) => COLOR_PALETTE[i % COLOR_PALETTE.length]))
          : (dataset.borderColor || color);

        return {
          ...dataset,
          backgroundColor: bgCol,
          borderColor: borderCol,
          fill: (type === 'line' || type === 'radar' || dataset.type === 'line') ? true : dataset.fill,
          tension: (type === 'line' || type === 'radar' || dataset.type === 'line') ? 0.4 : 0,
          borderWidth: (type === 'line' || type === 'radar' || dataset.type === 'line') ? 2.5 : 1,
          pointRadius: (type === 'line' || type === 'radar' || dataset.type === 'line') ? 4 : 0,
          pointHoverRadius: (type === 'line' || type === 'radar' || dataset.type === 'line') ? 6 : 0,
          pointBackgroundColor: '#ffffff',
          pointBorderWidth: 2,
        };
      })
    };
  }, [data, type]);

  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: type === 'horizontalBar' ? 'y' : 'x',
    radius: (type === 'pie' || type === 'doughnut' || type === 'polarArea') ? '85%' : undefined,
    layout: {
      padding: (type === 'pie' || type === 'doughnut' || type === 'polarArea')
        ? { top: 32, bottom: 32, left: 42, right: 42 }
        : 0
    },
    onClick: (event, elements, chart) => {
      if (onClick && elements.length > 0) {
        const elementIndex = elements[0].index;
        const datasetIndex = elements[0].datasetIndex;
        const label = chart.data.labels[elementIndex];
        const datasetLabel = chart.data.datasets[datasetIndex].label;
        onClick(label, datasetLabel);
      }
    },
    onHover: (event, elements, chart) => {
      if (onClick) {
        chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
      }
    },
    plugins: {
      legend: {
        display: hasY2Axis, // Show legend only if we have multiple dual axis datasets
        position: "top",
        align: "end",
        labels: {
          color: "#475569",
          font: { size: 11, weight: "600" },
          usePointStyle: true,
          boxWidth: 6,
          padding: 12,
        },
      },
      tooltip: {
        mode: type === 'pie' || type === 'doughnut' ? 'nearest' : 'index',
        intersect: true,
        backgroundColor: "#1e293b", // Premium Dark Slate
        padding: 10,
        cornerRadius: 6,
        titleFont: { size: 12, weight: "700" },
        bodyFont: { size: 11 },
      },
      datalabels: {
        display: type === 'line' || hasY2Axis,
        color: '#475569',
        anchor: 'end',
        align: 'top',
        offset: 4,
        formatter: (value) => {
          return Number(value).toLocaleString();
        },
        font: {
          weight: 'bold',
          size: 10
        }
      }
    },
    scales: (type === 'bar' || type === 'horizontalBar' || type === 'line') ? {
      x: {
        stacked: stacked,
        grid: { display: type === 'horizontalBar' ? true : false, color: "#f1f5f9" },
        ticks: { color: "#64748b", font: { size: 11, weight: "500" } },
      },
      y: {
        stacked: stacked,
        grid: { display: type === 'horizontalBar' ? false : true, color: "#f1f5f9" },
        ticks: { color: "#64748b", font: { size: 11, weight: "500" }, beginAtZero: true },
        grace: type === 'horizontalBar' ? undefined : '15%'
      },
      ...(hasY2Axis ? {
        y2: {
          type: 'linear',
          display: true,
          position: 'right',
          grid: { drawOnChartArea: false },
          ticks: { color: "#4f46e5", font: { size: 11, weight: "600" }, beginAtZero: true }
        }
      } : {})
    } : {},
    elements: {
      bar: { borderRadius: stacked ? 0 : 4 },
      line: { tension: 0.3, borderWidth: 2 }
    }
  };

  // Custom native plugin to automatically calculate values and draw totals on top of bars
  const topTotalLabelsPlugin = {
    id: 'topTotalLabels',
    afterDatasetsDraw(chart) {
      if (type !== 'bar' && type !== 'horizontalBar') return;
      const { ctx, data } = chart;
      ctx.save();

      const meta = chart.getDatasetMeta(data.datasets.length - 1);

      data.labels.forEach((label, index) => {
        // Sum values across all active datasets for each item index
        let total = 0;
        data.datasets.forEach((dataset) => {
          if (dataset.yAxisID === 'y2') return;
          const val = dataset.data[index];
          if (val) total += val;
        });

        const bar = meta.data[index];
        if (!bar) return;

        ctx.font = 'bold 10px monospace';
        ctx.fillStyle = '#475569'; // Clean Slate color for top metrics
        
        if (type === 'horizontalBar') {
          const xPos = bar.x + 8;
          const yPos = bar.y;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(total.toLocaleString(), xPos, yPos);
        } else {
          // Position the text cleanly 8px right over the peak of the bar element
          const xPos = bar.x;
          const yPos = bar.y - 8;
          ctx.textAlign = 'center';
          ctx.fillText(total.toLocaleString(), xPos, yPos);
        }
      });
      ctx.restore();
    }
  };

  if (showHeader === false) {
    return (
      <div ref={chartRef} style={{ height: `${height}px`, width: "100%", position: "relative" }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', height: '100%', justifyContent: 'center', p: 1 }}>
            <Skeleton variant="rectangular" height="80%" width="100%" sx={{ borderRadius: 2, bgcolor: 'rgba(0,0,0,0.04)' }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', px: 1 }}>
              <Skeleton variant="text" width="18%" sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
              <Skeleton variant="text" width="18%" sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
              <Skeleton variant="text" width="18%" sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
              <Skeleton variant="text" width="18%" sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
            </Box>
          </Box>
        ) : injectedData ? (
          isVisible && <ChartComponent
            data={injectedData}
            options={defaultOptions}
            plugins={[topTotalLabelsPlugin, ChartDataLabels, pieOutlabelsPlugin]}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full border border-dashed border-slate-200 rounded-lg bg-slate-50/50">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
              No Data Available
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <Box
      className="bg-white flex flex-col h-full"
      sx={{
        border: "1px solid #e2e8f0",
        borderRadius: "10px",
        overflow: "hidden",
        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)"
      }}
    >
      {/* Structural Header Wrapper */}
      <div className="px-4 py-2 bg-[#f8fafc] border-b border-slate-200 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Assessment className="text-slate-400" style={{ fontSize: "1.15rem" }} />
          <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
            {title} 
          </h3>
          {loading && <CircularProgress size={12} sx={{ ml: 1, color: "#4f46e5" }} />}
        </div>

        {onDownload && (
          <MuiTooltip title="Download Report" arrow>
            <IconButton
              onClick={onDownload}
              size="small"
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                bgcolor: "white",
                padding: "4px",
                "&:hover": { bgcolor: "#f1f5f9", borderColor: "#cbd5e1" }
              }}
            >
              <Download style={{ fontSize: "1rem" }} className="text-slate-600" />
            </IconButton>
          </MuiTooltip>
        )}
      </div>

      <div className="p-4 bg-white flex-grow flex items-center">
        <div ref={chartRef} style={{ height: `${height}px`, width: "100%", position: "relative" }}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', height: '100%', justifyContent: 'center', p: 1 }}>
              <Skeleton variant="rectangular" height="80%" width="100%" sx={{ borderRadius: 2, bgcolor: 'rgba(0,0,0,0.04)' }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', px: 1 }}>
                <Skeleton variant="text" width="18%" sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
                <Skeleton variant="text" width="18%" sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
                <Skeleton variant="text" width="18%" sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
                <Skeleton variant="text" width="18%" sx={{ bgcolor: 'rgba(0,0,0,0.04)' }} />
              </Box>
            </Box>
          ) : injectedData ? (
            isVisible && <ChartComponent
              data={injectedData}
              options={defaultOptions}
              plugins={[topTotalLabelsPlugin, ChartDataLabels, pieOutlabelsPlugin]}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full border border-dashed border-slate-200 rounded-lg bg-slate-50/50">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                No Data Available
              </span>
            </div>
          )}
        </div>
      </div>
    </Box>
  );
};

export default DashboardChart;